import { Component, EventEmitter, OnInit, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Genders } from 'src/app/constants/genders';
import { MaskPatterns, MaskType } from 'src/app/constants/masks';
import { USStates } from 'src/app/constants/us-states';
import { Member, UpdateMemberProfile } from 'src/app/models/member';
import { FormValidationService } from 'src/app/services/form-validation.service';
import { HelperService } from 'src/app/services/helper.service';
import { SessionService } from 'src/app/services/storage/session.service';
import { DialogPasswordComponent } from '../dialog/password.component';
import { DialogRegisteredEmailComponent } from '../dialog/registered-email.component';

@Component({
    selector: 'mwp-profile-form',
    templateUrl: './profile-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProfileFormComponent implements OnInit {
    @Input() public member: Member;
    @Input() public displayContinueButton: boolean;
    @Input() public enablePopupButtons: boolean;

    @Output() public emitContinue: EventEmitter<any> = new EventEmitter();
    @Output() public emitSave: EventEmitter<UpdateMemberProfile> = new EventEmitter();
    @Output() public emitDelete: EventEmitter<UpdateMemberProfile> = new EventEmitter();
    @Output() public emitReload: EventEmitter<any> = new EventEmitter();

    public maskType = MaskType;
    public genders = Genders;
    public usStates = USStates;
    public profileForm: FormGroup;
    public isContinueButton: boolean;
    public isPopupButtonsEnabled: boolean;

    constructor(
        private formBuilder: FormBuilder,
        private helperService: HelperService,
        private sessionService: SessionService,
        private dialog: MatDialog,
    ) {
    }

    ngOnInit(): void {
        this.isPopupButtonsEnabled = this.enablePopupButtons && !!this.sessionService.session.member && !this.sessionService.session.agent;
        this.profileForm = this.formBuilder.group({
            number: [{ value: this.member.number, disabled: this.member.numberReadOnly }],
            erid: [{ value: this.member.erid, disabled: this.member.eridReadOnly }],
            enrollmentUID: [{ value: this.member.enrollmentUID, disabled: true }],
            name: [{ value: this.member.name, disabled: this.member.nameReadOnly }],
            dobDecrypted: [{ value: this.member.dobDecrypted, disabled: this.member.dobDecryptedReadOnly }],
            ssnDecrypted: [{ value: this.member.ssnDecrypted, disabled: this.member.ssnDecryptedReadOnly }],
            homeEmail: [{ value: this.member.homeEmail, disabled: this.member.homeEmailReadOnly }, [Validators.required, Validators.compose([FormValidationService.validEmail(true)])]],
            homePhone: [{ value: this.member.homePhone, disabled: this.member.homeEmailReadOnly }, [Validators.compose([FormValidationService.validPhoneNumber(false)])]],
            cellPhone: [{ value: this.member.cellPhone, disabled: this.member.cellPhoneReadOnly }, [Validators.compose([FormValidationService.validPhoneNumber(false)])]],
            homeAddressLine1: [{ value: this.member.homeAddressLine1, disabled: this.member.addressReadOnly }, Validators.required],
            homeAddressLine2: [{ value: this.member.homeAddressLine2, disabled: this.member.addressReadOnly }],
            homeCity: [{ value: this.member.homeCity, disabled: this.member.homeCityReadOnly }, Validators.required],
            homeState: [{ value: this.member.homeState, disabled: this.member.homeStateReadOnly }, Validators.required],
            homeZipCode: [{ value: this.member.homeZipCode, disabled: this.member.homeZipCodeReadOnly }, [Validators.required, Validators.compose([FormValidationService.validZipcode(true)])]],
            gender: [{ value: this.member.gender, disabled: this.member.genderReadOnly }],
            dateOfHire: [{ value: this.member.dateOfHire, disabled: this.member.dateOfHireReadOnly }],
            workJobTitle: [{ value: this.member.workJobTitle, disabled: this.member.workJobTitleReadOnly }],
            workHours: [{ value: this.member.workHours, disabled: this.member.workHoursReadOnly }],
            companyLocation: [{ value: this.member.companyLocation, disabled: this.member.companyLocationReadOnly }],
            displayDivision: [{ value: this.member.displayDivision, disabled: this.member.divisionReadOnly }],
            displayEEClass1: [{ value: this.member.displayEEClass1, disabled: this.member.eeClass1ReadOnly }],
            displayEEClass2: [{ value: this.member.displayEEClass2, disabled: this.member.eeClass2ReadOnly }],
            displayEEClass3: [{ value: this.member.displayEEClass3, disabled: this.member.eeClass3ReadOnly }],
            displayEEClass4: [{ value: this.member.displayEEClass4, disabled: this.member.eeClass4ReadOnly }],
            displayEEClass5: [{ value: this.member.displayEEClass5, disabled: this.member.eeClass5ReadOnly }],
            department: [{ value: this.member.department, disabled: this.member.departmentReadOnly }],
            position: [{ value: this.member.position, disabled: this.member.positionReadOnly }],
            employmentStatus: [{ value: this.member.employmentStatus, disabled: this.member.employmentStatusReadOnly }],
            displayUDF1: [{ value: this.member.displayUDF1, disabled: this.member.udf1ReadOnly }],
            displayUDF2: [{ value: this.member.displayUDF2, disabled: this.member.udf2ReadOnly }],
            displayUDF3: [{ value: this.member.displayUDF3, disabled: this.member.udf3ReadOnly }],
            displayUDF4: [{ value: this.member.displayUDF4, disabled: this.member.udf4ReadOnly }],
            displayUDF5: [{ value: this.member.displayUDF5, disabled: this.member.udf5ReadOnly }],
        });
    }

    public submitForm(): void {
        if (this.displayContinueButton && this.profileForm.pristine) {
            this.emitContinue.emit();
            return;
        }

        if (this.profileForm.invalid) {
            this.profileForm.updateValueAndValidity();
            return;
        }

        let l_UpdateMemberProfile = new UpdateMemberProfile();
        l_UpdateMemberProfile = this.helperService.convertObjectIntoObject(this.member, l_UpdateMemberProfile);
        l_UpdateMemberProfile = this.helperService.convertObjectIntoObject(this.profileForm.value, l_UpdateMemberProfile);

        if (l_UpdateMemberProfile.cellPhone)
            l_UpdateMemberProfile.cellPhone = l_UpdateMemberProfile.cellPhone.replace(MaskPatterns.NON_DECIMAL_DIGITS, '');

        if (l_UpdateMemberProfile.homePhone)
            l_UpdateMemberProfile.homePhone = l_UpdateMemberProfile.homePhone.replace(MaskPatterns.NON_DECIMAL_DIGITS, '');

        if (l_UpdateMemberProfile.homeZipCode)
            l_UpdateMemberProfile.homeZipCode = l_UpdateMemberProfile.homeZipCode.replace(MaskPatterns.NON_DECIMAL_DIGITS, '');

        this.emitSave.emit(l_UpdateMemberProfile);
    }

    public deleteAccount(): void {
        this.emitDelete.emit();
    }

    public openUpdatePasswordDialog(): void {
        this.dialog.open(DialogPasswordComponent, { panelClass: 'mwp-modal-override-300' });
    }

    public openUpdateRegisteredEmail(): void {
        const l_dialogRef = this.dialog.open(DialogRegisteredEmailComponent, { panelClass: 'mwp-modal-override-300' });
        l_dialogRef.afterClosed().subscribe((response: any) => {
            if (response) {
                this.emitReload.emit();
            }
        });;
    }
}